/**

© 2025 Indikin.online. All rights reserved.

Made with❤️ by YalaCoder.

--- REWRITE SUMMARY (V4 - Register Analytics Modal) ---

MISSION: To register the new AnalyticsModal with the
central modal provider.

THE FIX:

NEW LAZY IMPORT: The AnalyticsModal is now lazy-loaded
at the top of the file.

NEW SWITCH CASE: A new case, 'view-analytics', has been
added to the renderActiveModal function. It renders the
new component and passes all necessary props from modalData.

RESULT: The modal system can now be triggered by
openModal('view-analytics', { ... }) from any component.
*/

import React, { useState, useCallback, useEffect, createContext, useContext, Suspense } from 'react';
import ModalLoading from '@/components/shared/ModalLoading';

// --- Lazy-loaded Modal Components ---
const WalletModal = React.lazy(() => import('../components/modals/WalletModal'));
const LitepaperModal = React.lazy(() => import('../components/modals/LitepaperModal'));
const DonateModal = React.lazy(() => import('../components/modals/DonateModal'));
const FilmmakerApplyModal = React.lazy(() => import('../components/modals/FilmmakerApplyModal'));
const VideoPlayerModal = React.lazy(() => import('../components/modals/VideoPlayerModal'));
const PaymentModal = React.lazy(() => import('../components/modals/PaymentModal'));
const IndikinPassModal = React.lazy(() => import('../components/modals/IndikinPassModal'));
const AngelPassBenefitsModal = React.lazy(() => import('../components/modals/AngelPassBenefitsModal'));
const EditSeriesModal = React.lazy(() => import('../components/dashboard/filmmaker/EditSeriesModal'));
const ShareModal = React.lazy(() => import('../components/modals/ShareModal'));
const ConfirmationModal = React.lazy(() => import('../components/shared/ConfirmationModal'));
const AnalyticsModal = React.lazy(() => import('../components/dashboard/filmmaker/AnalyticsModal'));
const GlobalAiModal = React.lazy(() => import('../components/modals/GlobalAiModal')); // <-- NEW // <-- ADD THIS
const ExitIntentModal = React.lazy(() => import('../components/modals/ExitIntentModal'));

const ModalContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components
export const useModal = () => useContext(ModalContext);

export const ModalProvider = ({ children }) => {
  const [activeModal, setActiveModal] = useState(null);
  const [modalData, setModalData] = useState({});
  // --- NEW: Separate state for AI Chat ---
  const [isAiChatOpen, setIsAiChatOpen] = useState(false);
  const [videoCloseCount, setVideoCloseCount] = useState(0);

  const openModal = useCallback((modalName, data = {}) => {
    // Special handling for ai-chat to allow it to be open ALONGSIDE other modals
    if (modalName === 'ai-chat') {
      setIsAiChatOpen(true);
      // We do NOT set activeModal for ai-chat, so it doesn't replace the current modal
      // We also don't block scrolling for ai-chat unless it's the only thing open?
      // Actually, GlobalAiModal is a sidebar, so we might want scrolling enabled generally if it's just the sidebar.
      // But if another modal is open, that modal controls the scrolling.
      // Let's keep existing logic: if ai-chat is the ONLY thing, enable scroll.
      if (!activeModal) {
        document.body.style.overflow = '';
      }
      return;
    }

    setActiveModal(modalName);
    setModalData(data);
    document.body.style.overflow = 'hidden';
  }, [activeModal]); // Added dependency on activeModal for the overflow logic

  const closeModal = useCallback((modalName) => {
    // If specific modalName provided (optional feature, but good for safety)
    if (modalName === 'ai-chat') {
      setIsAiChatOpen(false);
      // Reset overflow only if no other modal is active
      if (!activeModal) {
        document.body.style.overflow = '';
      }
      return;
    }

    // Track video modal closes for ResumePrompt re-evaluation
    if (activeModal === 'video') {
      setVideoCloseCount(c => c + 1);
    }

    // Default behavior: close main modal
    setActiveModal(null);
    setModalData({});

    // If AI chat is still open, we should technically allow scrolling again 
    // (since AI chat sidebar allows scrolling main page usually)
    // or maybe we just reset it.
    if (!isAiChatOpen) {
      document.body.style.overflow = '';
    } else {
      // AI Chat is open, allow scrolling
      document.body.style.overflow = '';
    }
  }, [activeModal, isAiChatOpen]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        // If main modal is open, close it
        if (activeModal) {
          closeModal();
        }
        // If NO main modal but AI chat is open, close AI chat? 
        // User behavior nuance: usually Esc closes the top-most thing. 
        // Let's say if activeModal exists, it takes precedence.
        // If only AiChat is open, close that.
        else if (isAiChatOpen) {
          setIsAiChatOpen(false);
          document.body.style.overflow = '';
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeModal, activeModal, isAiChatOpen]);

  const renderActiveModal = () => {
    // This new handler allows us to chain a custom callback before closing.
    const handleClose = () => {
      if (modalData.onCloseCallback) {
        modalData.onCloseCallback();
      }
      closeModal();
    };

    switch (activeModal) {
      case 'filmmaker-apply':
        return <FilmmakerApplyModal onClose={closeModal} />;
      case 'wallet':
        return <WalletModal onClose={closeModal} />;
      case 'litepaper':
        return <LitepaperModal onClose={closeModal} />;
      case 'donate':
        return <DonateModal onClose={closeModal} />;
      case 'video':
        return <VideoPlayerModal
          content={modalData.content}
          onClose={closeModal}
          startTime={modalData.startTime}
          autoResume={modalData.autoResume}
        />;

      case 'payment':
        return <PaymentModal
          tier={modalData.tier}
          initialTab={modalData.initialTab}
          resumptionData={modalData.resumptionData}
          type={modalData.type}
          contentId={modalData.contentId}
          price={modalData.price}
          title={modalData.title}
          thumbnailUrl={modalData.thumbnailUrl}
          onClose={handleClose}
        />;
      case 'indikin-pass':
        return <IndikinPassModal onClose={closeModal} requiredContent={modalData.requiredContent} isPreview={modalData.isPreview} />;
      case 'angel-pass-benefits':
        return <AngelPassBenefitsModal onClose={closeModal} />;
      case 'deleteAccount':
        return <ConfirmationModal isOpen={true} onClose={closeModal} {...modalData} />;
      case 'edit-series':
        return <EditSeriesModal seriesId={modalData.seriesId} onClose={closeModal} />;
      case 'share-content':
        return <ShareModal type={modalData.type} id={modalData.id} title={modalData.title} onClose={closeModal} />;
      case 'view-analytics':
        return <AnalyticsModal uids={modalData.uids} title={modalData.title} onClose={closeModal} />;
      // Removed 'ai-chat' case from here
      case 'exitIntent':
        return <ExitIntentModal onClose={closeModal} />;
    }
  };

  return (
    <ModalContext.Provider value={{ openModal, closeModal, videoCloseCount, activeModal }}>
      {children}
      <Suspense fallback={<ModalLoading />}>
        {renderActiveModal()}
        {/* Render AI Chat independently */}
        {isAiChatOpen && (
          <GlobalAiModal
            onClose={() => closeModal('ai-chat')}
            hasBackdrop={!activeModal} // Only show backdrop if NO other modal is present
          />
        )}
      </Suspense>
    </ModalContext.Provider>
  );
};
